import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TierBadge } from "@/components/TierBadge";
import { REGIONS } from "@/lib/tft";

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({
    riotId: "",
    region: "euw",
    nickname: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/players");
      setPlayers(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onAdd = async (e) => {
    e.preventDefault();
    const raw = form.riotId.trim();
    if (!raw.includes("#")) {
      toast.error("Riot ID musi mieć format Nick#Tag (np. Guzikson#EUWE)");
      return;
    }
    const [game_name, tag_line] = raw.split("#");
    if (!game_name || !tag_line) {
      toast.error("Niepoprawny Riot ID");
      return;
    }
    setAdding(true);
    try {
      await api.post("/players", {
        game_name: game_name.trim(),
        tag_line: tag_line.trim(),
        region: form.region,
        nickname: form.nickname.trim() || null,
      });
      toast.success(`Dodano gracza ${game_name}#${tag_line}`);
      setForm({ riotId: "", region: form.region, nickname: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async (player) => {
    if (!window.confirm(`Usunąć gracza ${player.game_name}#${player.tag_line}?`)) return;
    setBusyId(player.id);
    try {
      await api.delete(`/players/${player.id}`);
      toast.success("Gracz usunięty");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onRefresh = async (player) => {
    setBusyId(player.id);
    try {
      await api.post(`/players/${player.id}/refresh`);
      toast.success(`Zaktualizowano ${player.game_name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.post("/players/refresh-all");
      const { refreshed, total, errors } = res.data;
      if (errors.length > 0) {
        toast.warning(`Odświeżono ${refreshed}/${total}. Błędy: ${errors.length}`);
      } else {
        toast.success(`Odświeżono ${refreshed}/${total} graczy`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#0AC8B9] font-bold mb-2">
            Panel Admina
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-black tracking-tight text-[#F0E6D2]">
            Zarządzanie turniejem
          </h1>
          <p className="text-sm text-[#A09B8C] mt-1">
            Dodawaj graczy z lolchess.gg i odświeżaj ich statystyki.
          </p>
        </div>
        <button
          data-testid="refresh-all-button"
          onClick={onRefreshAll}
          disabled={refreshingAll || players.length === 0}
          className="px-5 h-10 rounded-md bg-[#0AC8B9] hover:bg-[#3FE3D6] text-[#0A0A0B] font-bold transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {refreshingAll ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Odśwież wszystkich
        </button>
      </div>

      {/* Add player form */}
      <form
        onSubmit={onAdd}
        className="rounded-2xl border border-[#2A2A30] bg-[#121214] p-6 md:p-8 mb-8"
      >
        <div className="flex items-center gap-2 mb-5">
          <UserPlus className="h-5 w-5 text-[#C8AA6E]" />
          <h2 className="font-[Outfit] text-xl font-bold text-[#F0E6D2]">
            Dodaj gracza
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#6E6A5E] font-bold mb-2 block">
              Riot ID
            </label>
            <Input
              data-testid="player-riotid-input"
              required
              value={form.riotId}
              onChange={(e) => setForm({ ...form, riotId: e.target.value })}
              placeholder="Guzikson#EUWE"
              className="bg-[#0A0A0B] border-[#2A2A30] text-[#F0E6D2] h-11"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#6E6A5E] font-bold mb-2 block">
              Region
            </label>
            <Select
              value={form.region}
              onValueChange={(v) => setForm({ ...form, region: v })}
            >
              <SelectTrigger
                data-testid="player-region-select"
                className="bg-[#0A0A0B] border-[#2A2A30] text-[#F0E6D2] h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-[#2A2A30] text-[#F0E6D2]">
                {REGIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#6E6A5E] font-bold mb-2 block">
              Pseudonim (opcjonalnie)
            </label>
            <Input
              data-testid="player-nickname-input"
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              placeholder="np. Kapitan"
              className="bg-[#0A0A0B] border-[#2A2A30] text-[#F0E6D2] h-11"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <button
              type="submit"
              data-testid="add-player-button"
              disabled={adding}
              className="w-full h-11 rounded-md bg-[#C8AA6E] hover:bg-[#DCC28A] text-[#0A0A0B] font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Dodaj
            </button>
          </div>
        </div>
      </form>

      {/* Players table */}
      <div className="rounded-2xl border border-[#2A2A30] bg-[#121214] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2A2A30] flex items-center justify-between">
          <h2 className="font-[Outfit] text-lg font-bold text-[#F0E6D2]">
            Gracze ({players.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-[#6E6A5E]">Ładowanie...</div>
        ) : players.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[#A09B8C]">Brak graczy. Dodaj pierwszego powyżej.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#2A2A30]/60">
            {players.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-player-${p.id}`}
                className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-6 py-4 hover:bg-[#1A1A1D] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {p.profile_icon_url ? (
                    <img
                      src={p.profile_icon_url}
                      alt=""
                      className="h-10 w-10 rounded-md border border-[#2A2A30]"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-[#1A1A1D] border border-[#2A2A30]" />
                  )}
                  <div className="min-w-0">
                    <Link
                      to={`/player/${p.id}`}
                      className="font-bold text-[#F0E6D2] hover:text-[#C8AA6E] truncate block"
                    >
                      {p.nickname || p.game_name}
                    </Link>
                    <div className="text-xs text-[#6E6A5E]">
                      {p.game_name}#{p.tag_line} · {p.region.toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <TierBadge tier={p.tier} rank={p.rank} lp={p.league_points} />
                  <a
                    href={`https://lolchess.gg/profile/${p.region}/${p.game_name}-${p.tag_line}/set17`}
                    target="_blank"
                    rel="noreferrer"
                    title="Otwórz na lolchess.gg"
                    className="h-9 w-9 rounded-md border border-[#2A2A30] hover:border-[#0AC8B9] flex items-center justify-center text-[#A09B8C] hover:text-[#0AC8B9] transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    data-testid={`refresh-player-${p.id}`}
                    onClick={() => onRefresh(p)}
                    disabled={busyId === p.id}
                    title="Odśwież statystyki"
                    className="h-9 w-9 rounded-md border border-[#2A2A30] hover:border-[#C8AA6E] flex items-center justify-center text-[#A09B8C] hover:text-[#C8AA6E] transition-colors disabled:opacity-50"
                  >
                    {busyId === p.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    data-testid={`delete-player-${p.id}`}
                    onClick={() => onDelete(p)}
                    disabled={busyId === p.id}
                    title="Usuń"
                    className="h-9 w-9 rounded-md border border-[#2A2A30] hover:border-[#E84057] flex items-center justify-center text-[#A09B8C] hover:text-[#E84057] transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
